<template>
  <div class="users">
    <mainmenu selected="users"></mainmenu>

    <div class="users-shell">
      <div class="users-toolbar">
        <h1>{{ $t("users.page_title") }}</h1>
        <el-button type="primary" icon="el-icon-plus" @click='add_item()'>{{ $t("add_item") }}</el-button>
      </div>

      <div class="users-filters">
        <div class="filters-title">{{ $t("users.filters") }}</div>
        <el-input
          class="filter-control filter-search"
          v-model="Filters.Search"
          :placeholder="$t('users.search_placeholder')"
          prefix-icon="el-icon-search"
          clearable
          @input="on_filter_input"
          @clear="apply_filters">
        </el-input>
        <el-select class="filter-control" v-model="Filters.UserGroup" :placeholder="$t('users.user_group')" clearable filterable @change="apply_filters">
          <el-option v-for="item in FilterOptions.UserGroups" :key="item" :label="item" :value="item"></el-option>
        </el-select>
        <el-select class="filter-control" v-model="Filters.PlantCountry" :placeholder="$t('users.factory_country')" clearable filterable @change="apply_filters">
          <el-option v-for="item in FilterOptions.PlantCountries" :key="item" :label="country_label(item)" :value="item"></el-option>
        </el-select>
        <el-select class="filter-control" v-model="Filters.PlantCode" :placeholder="$t('users.factory_code')" clearable filterable @change="apply_filters">
          <el-option v-for="item in FilterOptions.PlantCodes" :key="item" :label="item" :value="item"></el-option>
        </el-select>
        <el-select class="filter-control status-filter" v-model="Filters.SsoStatus" :placeholder="$t('users.sso_status')" clearable @change="apply_filters">
          <el-option v-for="item in FilterOptions.SsoStatuses" :key="item" :label="format_sso_status(item)" :value="item"></el-option>
        </el-select>
        <el-button class="reset-filters" icon="el-icon-refresh-left" @click="reset_filters">{{ $t("reset") }}</el-button>
      </div>

      <div class="users-card">
      <el-table
        class="users-table"
        :data="Results"
        style="width: 100%"
        :max-height="TableMaxHeight"
        stripe
        v-loading="tableLoadingVisible && !HasLoadedUsers">
        <el-table-column prop="Name" :label="$t('users.name')" width="190" show-overflow-tooltip></el-table-column>
        
        <el-table-column prop="UserType" :label="$t('users.user_group')" width="155" show-overflow-tooltip></el-table-column>

        <el-table-column prop="Email" :label="$t('users.email')" min-width="260" show-overflow-tooltip></el-table-column>
        <el-table-column prop="SsoStatus" :label="$t('users.sso_status')" width="120">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.SsoStatus == 'approved' ? 'success' : (scope.row.SsoStatus == 'blocked' ? 'danger' : 'warning')">
              {{ format_sso_status(scope.row.SsoStatus || 'approved') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('users.access_request')" min-width="240" show-overflow-tooltip>
          <template slot-scope="scope">
            <div v-if="scope.row.AccessRequestId" class="request-summary">
              <strong>{{ request_location(scope.row) || $t("users.no_location_selected") }}</strong>
              <span v-if="scope.row.AccessRequestedAt">{{ scope.row.AccessRequestedAt }}</span>
              <el-popover v-if="scope.row.AccessRequestMessage" placement="top" width="320" trigger="hover">
                <p class="request-message">{{ scope.row.AccessRequestMessage }}</p>
                <el-button slot="reference" type="text" size="mini">{{ $t("users.message") }}</el-button>
              </el-popover>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="SsoLastLogin" :label="$t('users.sso_last_login')" width="150" show-overflow-tooltip></el-table-column>
        <el-table-column :label="$t('users.factory_country')" min-width="210" show-overflow-tooltip>
          <template slot-scope="scope">
            <span>{{ country_list_label(scope.row.FactoryCountry) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('users.assigned_plants')" width="225">
          <template slot-scope="scope">
            <div class="factory-tags">
              <el-tag
                v-for="code in split_factory_values(scope.row.FactoryShortCode)"
                :key="scope.row.Id + '-' + code"
                size="mini"
                :type="is_primary_factory(scope.row, code) ? 'success' : 'info'"
                effect="plain">
                {{ code }}<span v-if="is_primary_factory(scope.row, code)" class="primary-suffix"> {{ $t('users.primary_factory') }}</span>
              </el-tag>
            </div>
          </template>
        </el-table-column>

        <el-table-column fixed="right" :label="$t('operations')" width="245" align="right" header-align="right">
          <template slot-scope="scope">
            <div class="operation-actions">
              <el-button v-if="can_approve(scope.row)" @click="approve_access(scope.row)" type="text" size="mini">{{ $t("users.approve") }}</el-button>
              <el-button v-if="can_approve(scope.row)" @click="block_access(scope.row)" type="text" size="mini" class="danger-action">{{ $t("users.block") }}</el-button>
              <el-button @click="edit_item(scope.row)" type="text" size="mini">{{ $t("edit") }}</el-button>
              <el-button @click="delete_item(scope.row)" type="text" size="mini" class="danger-action">{{ $t("delete") }}</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

        <div class="users-pagination">
          <el-pagination
          background
          @size-change        = "on_page_size_change"
          @current-change     = "on_page_change"
          :page-size.sync     = "PaginationInfo.PerPage"
          :current-page.sync  = "PaginationInfo.Page"
          :total              = "PaginationInfo.RowCount"
          layout              = "total, prev, pager, next">
          </el-pagination>
        </div>
      </div>
    </div>
    <user-dialog ref="item_dialog" @save='refresh_info(true)' />
  </div>
</template>

<script>
import MainMenu from "./../widgets/MainMenu";
import settings from "./../backend/LocalSettings";
import UserDialog from "./../views/dialogs/UserDialog";
import { countryLabel, countryListLabel } from "./../utils/countries";

export default {
  name: "users",
  components: {
    "mainmenu": MainMenu,
    "user-dialog": UserDialog
  },
  data () {
    return { 
      tableLoadingVisible: false,
      Results: [],
      AllResults: [],
      HasLoadedUsers: false,
      Filters: { Search: "", UserGroup: "", PlantCountry: "", PlantCode: "", SsoStatus: "" },
      FilterOptions: { UserGroups: [], PlantCountries: [], PlantCodes: [], SsoStatuses: [] },
      TableMaxHeight: 620,
      ResizeTimer: null,
      FilterTimer: null,
      RequestSerial: 0,
      PaginationInfo: { Page: 1, PerPage: 20, RowCount: 0, PageSizes: [20] }
    }
  },
  methods: {
    country_label: function(country) {
      return countryLabel(this, country);
    },
    country_list_label: function(countries) {
      return countryListLabel(this, countries);
    },
    get_default_filters: function() {
      return { Search: "", UserGroup: "", PlantCountry: "", PlantCode: "", SsoStatus: "" };
    },
    get_visible_page_size: function() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 900;
      var cardElement = this.$el ? this.$el.querySelector(".users-card") : null;
      var cardTop = cardElement ? cardElement.getBoundingClientRect().top : 205;
      var paginationHeight = 52;
      var bottomPadding = 18;
      var availableTableHeight = Math.max(320, viewportHeight - cardTop - paginationHeight - bottomPadding);
      this.TableMaxHeight = availableTableHeight;

      var rowHeight = 33;
      var tableHeaderHeight = 44;
      var visibleRows = Math.floor((availableTableHeight - tableHeaderHeight) / rowHeight);
      return Math.max(10, Math.min(22, visibleRows));
    },
    fit_page_size_to_viewport: function() {
      var pageSize = this.get_visible_page_size();
      this.PaginationInfo.PageSizes = [pageSize];
      if( this.PaginationInfo.PerPage != pageSize )
      {
        this.PaginationInfo.PerPage = pageSize;
        return true;
      }
      return false;
    },
    on_window_resize: function() {
      clearTimeout(this.ResizeTimer);
      this.ResizeTimer = setTimeout(() => {
        if( this.fit_page_size_to_viewport() )
        {
          this.PaginationInfo.Page = 1;
          this.refresh_info();
        }
      }, 150);
    },
    apply_filters: function() {
      this.PaginationInfo.Page = 1;
      this.apply_client_filters();
    },
    on_filter_input: function() {
      clearTimeout(this.FilterTimer);
      this.FilterTimer = setTimeout(() => {
        this.apply_filters();
      }, 120);
    },
    reset_filters: function() {
      clearTimeout(this.FilterTimer);
      this.Filters = this.get_default_filters();
      this.apply_filters();
    },
    format_sso_status: function(status) {
      if(!status) return "";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    split_factory_values: function(value) {
      if(!value) return [];
      return value.split(",").map((item) => item.trim()).filter((item) => item !== "");
    },
    is_primary_factory: function(row, code) {
      return (row.PrimaryFactoryShortCode || "").trim() === code;
    },
    request_location: function(row) {
      var parts = [];
      if(row.AccessRequestedCountry) parts.push(this.country_label(row.AccessRequestedCountry));
      if(row.AccessRequestedFactoryCode) parts.push(row.AccessRequestedFactoryCode);
      return parts.join(" / ");
    },
    can_approve: function(row) {
      return (row.SsoStatus || "approved") === "pending" && this.$rights.is_admin_or_power_user();
    },
    normalize_filter_text: function(value) {
      return (value || "").toString().trim().toLowerCase();
    },
    split_filter_values: function(value) {
      return this.split_factory_values(value).map((item) => item.trim()).filter((item) => item !== "");
    },
    row_matches_filters: function(row) {
      var search = this.normalize_filter_text(this.Filters.Search);
      if(search !== "")
      {
        var searchable = this.normalize_filter_text((row.Name || "") + " " + (row.Email || ""));
        if(searchable.indexOf(search) === -1) return false;
      }

      if(this.Filters.UserGroup && (row.UserType || "").trim() !== this.Filters.UserGroup.trim()) return false;

      if(this.Filters.PlantCountry)
      {
        var countries = this.split_filter_values(row.FactoryCountry);
        if(countries.indexOf(this.Filters.PlantCountry) === -1) return false;
      }

      if(this.Filters.PlantCode)
      {
        var codes = this.split_filter_values(row.FactoryShortCode);
        if(codes.indexOf(this.Filters.PlantCode) === -1) return false;
      }

      if(this.Filters.SsoStatus && (row.SsoStatus || "approved") !== this.Filters.SsoStatus) return false;

      return true;
    },
    apply_client_filters: function() {
      if(!this.HasLoadedUsers)
      {
        this.refresh_info(true);
        return;
      }

      var filtered = this.AllResults.filter((row) => this.row_matches_filters(row));
      var pageSize = this.PaginationInfo.PerPage;
      var maxPage = Math.max(1, Math.ceil(filtered.length / pageSize));
      if(this.PaginationInfo.Page > maxPage) this.PaginationInfo.Page = maxPage;

      var start = (this.PaginationInfo.Page - 1) * pageSize;
      this.Results = filtered.slice(start, start + pageSize);
      this.PaginationInfo.RowCount = filtered.length;
    },
    on_page_change: function(page) {
      if(page) this.PaginationInfo.Page = page;
      this.apply_client_filters();
    },
    on_page_size_change: function(pageSize) {
      if(pageSize) this.PaginationInfo.PerPage = pageSize;
      this.PaginationInfo.Page = 1;
      this.apply_client_filters();
    },
     refresh_info: function(forceFetch) {
       this.fit_page_size_to_viewport();
       if(this.HasLoadedUsers && forceFetch !== true)
       {
         this.apply_client_filters();
         return;
       }

       var requestSerial = ++this.RequestSerial;
       var serverPaginationInfo = { Page: 1, PerPage: 10000, RowCount: 0, PageSizes: [10000] };
       this.tableLoadingVisible = true;
       this.$http
          .post('users/refresh_info',  { Filters: this.get_default_filters(), PaginationInfo: serverPaginationInfo, IncludeFilterOptions: true } )
          .then(function (response) {
            if(requestSerial !== this.RequestSerial) return;
            this.tableLoadingVisible = false;
            if( settings.verify_response(response, this.$rights)) {
              this.AllResults = response.body.Results || [];
              this.HasLoadedUsers = true;
              this.FilterOptions = response.body.FilterOptions || this.FilterOptions;
              this.apply_client_filters();
            } else {
              this.$router.push("/");
            }
          })
          .catch(function() {
            if(requestSerial !== this.RequestSerial) return;
            this.tableLoadingVisible = false;
            this.$message.error(this.$t("users.load_failed"));
          });
    },
    add_item: function(){
      this.$refs.item_dialog.show_me();
    },
    edit_item: function(item){
      this.$refs.item_dialog.show_me(item);
    },
    delete_item: function(item){
       this.$confirm( this.$t("confirm_delete"), this.$t("warning_title"), { 
         confirmButtonText: this.$t("confirm"), 
         cancelButtonText: this.$t("cancel"), 
         type: 'warning'
        }).then(() => {
          this.tableLoadingVisible = true;
          this.$http
              .post('users/delete_item',  { object: item } )
              .then(function (response) {
                this.tableLoadingVisible = false;
                if( settings.verify_response(response, this.$rights)) {
                  this.refresh_info(true);
                } else {
                  this.$router.push("/");
                }
              });
        }).catch(()=>{ });
    },
    approve_access: function(item) {
      this.tableLoadingVisible = true;
      this.$http
        .post('users/approve_access', { id_user: item.Id, factory_id: item.AccessRequestedFactoryId || item.IdFactory })
        .then(function(response) {
          this.tableLoadingVisible = false;
          if(settings.verify_response(response, this.$rights)) {
            if(response.body.ErrorMessage) {
              this.$message.error(response.body.ErrorMessage);
            } else {
              this.$message.success("Access approved");
              this.refresh_info(true);
            }
          } else {
            this.$router.push("/");
          }
        }, function() {
          this.tableLoadingVisible = false;
          this.$message.error("Could not approve access.");
        });
    },
    block_access: function(item) {
      this.$confirm(this.$t("users.block_confirm"), this.$t("warning_title"), {
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("cancel"),
        type: 'warning'
      }).then(() => {
        this.tableLoadingVisible = true;
        this.$http
          .post('users/block_access', { id_user: item.Id })
          .then(function(response) {
            this.tableLoadingVisible = false;
            if(settings.verify_response(response, this.$rights)) {
              if(response.body.ErrorMessage) {
                this.$message.error(response.body.ErrorMessage);
              } else {
                this.$message.success("Access blocked");
                this.refresh_info(true);
              }
            } else {
              this.$router.push("/");
            }
          }, function() {
            this.tableLoadingVisible = false;
            this.$message.error("Could not block access.");
          });
      }).catch(()=>{ });
    }
  },
  mounted: function() {
    this.fit_page_size_to_viewport();
    window.addEventListener('resize', this.on_window_resize);
    this.refresh_info(true);
  },
  beforeDestroy: function() {
    window.removeEventListener('resize', this.on_window_resize);
    clearTimeout(this.ResizeTimer);
    clearTimeout(this.FilterTimer);
  }
};
</script>

<style lang="less" scoped>
.users-shell {
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px 18px;
}

.users-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid rgba(31, 73, 95, 0.14);
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(19, 55, 73, 0.10);

  h1 {
    margin: 0;
    color: #172b38;
    font-size: 30px;
    line-height: 1.1;
    font-weight: 700;
  }

  .el-button {
    height: 36px;
    padding: 0 16px;
    border-color: #157c91;
    background: #178da5;
    box-shadow: 0 6px 14px rgba(23, 141, 165, 0.22);
  }
}

.users-filters {
  display: grid;
  grid-template-columns: auto minmax(220px, 1.4fr) minmax(145px, 0.8fr) minmax(160px, 0.9fr) minmax(130px, 0.7fr) minmax(130px, 0.7fr) auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid rgba(31, 73, 95, 0.12);
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(19, 55, 73, 0.08);
}

.filters-title {
  color: #344955;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.filter-control {
  width: 100%;

  /deep/ .el-input__inner {
    height: 34px;
    border-color: #d9e4ea;
    color: #3f5361;
    font-weight: 500;
  }
}

.reset-filters {
  height: 34px;
  padding: 0 12px;
  color: #496372;
  border-color: #d9e4ea;
}

.users-card {
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid rgba(31, 73, 95, 0.14);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(19, 55, 73, 0.12);
}

.users-table {
  flex: 1;

  /deep/ th {
    padding: 8px 0 !important;
    background: #f5f8fa;
    color: #5b6873;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  /deep/ td {
    padding: 4px 0 !important;
    color: #4f5f69;
    border-bottom-color: #edf1f4;
  }

  /deep/ .cell {
    line-height: 22px;
    white-space: nowrap;
  }

  /deep/ .el-table__row {
    height: 33px;
  }

  /deep/ .el-table__fixed-right::before,
  /deep/ .el-table__fixed::before {
    background-color: #edf1f4;
  }

  /deep/ .el-loading-mask {
    background-color: rgba(255, 255, 255, 0.98);
  }
}

.factory-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  line-height: 1.4;
}

.primary-suffix {
  margin-left: 3px;
  font-weight: 700;
}

.request-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  strong {
    color: #334155;
    font-weight: 700;
  }

  span {
    color: #84919d;
    font-size: 12px;
  }
}

.request-message {
  margin: 0;
  color: #344854;
  line-height: 1.45;
  white-space: pre-wrap;
}

.operation-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  min-height: 24px;

  /deep/ .el-button {
    margin: 0;
    padding: 0;
    color: #167da0;
    font-weight: 600;
  }

  /deep/ .danger-action {
    color: #c8524b;
  }
}

.users-pagination {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 48px;
  padding: 8px 12px;
  background: #fbfcfd;
  border-top: 1px solid #edf1f4;

  /deep/ .el-pagination {
    padding: 0;
    font-weight: 600;
    white-space: nowrap;
  }
}

@media (max-width: 768px) {
  .users-shell {
    padding: 10px;
  }

  .users-toolbar {
    align-items: stretch;
    flex-direction: column;

    h1 {
      font-size: 26px;
    }
  }

  .users-filters {
    grid-template-columns: 1fr;
  }

  .users-pagination {
    justify-content: flex-start;
    overflow-x: auto;
  }
}

</style>
